import { Popover } from "@headlessui/react";
import { type Placement } from "@popperjs/core";
import classNames from "classnames";
import React, { type CSSProperties, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { usePopper } from "react-popper";
import { useScreenDimensions } from "~/hooks/useScreenDimensions";
import { isClient } from "~/lib/ssr";
import { theme } from "~/lib/theme";

type Props = React.PropsWithChildren<{
  content: JSX.Element | string;
  behavior?: "hover" | "static";
  placement?: Placement;
  className?: string;
  buttonClassName?: string;
  refClassName?: string;
  style?: CSSProperties;
  delay?: number;
  disabled?: boolean;
  disableOnMobile?: boolean;
  displayOverlay?: boolean;
  maxHeight?: string;
}>;

type TooltipState = "hidden" | "shown" | "showing";

export const LegacyTooltip: React.FC<Props> = ({
  content,
  behavior = "hover",
  placement,
  children,
  buttonClassName,
  refClassName,
  className,
  style,
  delay = 150,
  disabled = false,
  disableOnMobile = false,
  displayOverlay = true,
  maxHeight = "50vh",
}) => {
  const [tooltipState, setTooltipState] = useState<TooltipState>(behavior === "hover" ? "hidden" : "shown");
  const dimensions = useScreenDimensions();
  const [referenceElement, setReferenceElement] = useState<HTMLSpanElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 5],
        },
      },
      {
        name: "preventOverflow",
        options: {
          altAxis: true,
          mainAxis: true,
        },
      },
      {
        name: "arrow",
      },
    ],
  });

  useEffect(() => {
    if (tooltipState !== "showing") {
      return;
    }

    const timeout = setTimeout(() => {
      setTooltipState("shown");
    }, delay);

    return () => {
      return clearTimeout(timeout);
    };
  }, [tooltipState, delay]);

  if (disabled) {
    return (
      <span className={className} style={style}>
        {children}
      </span>
    );
  }

  return (
    <Popover className={className} style={style}>
      <Popover.Button
        as="div"
        onMouseEnter={() => {
          if (disableOnMobile && dimensions.width < parseInt(theme.screens.sm as string)) {
            return;
          }

          if (behavior !== "hover") {
            return;
          }

          setTooltipState("showing");
        }}
        onMouseLeave={() => {
          if (behavior !== "hover") {
            return;
          }

          setTooltipState("hidden");
        }}
        className={buttonClassName}
      >
        <div className={refClassName} ref={setReferenceElement}>
          {children}
        </div>
      </Popover.Button>

      {tooltipState === "shown" &&
        displayOverlay &&
        isClient &&
        ReactDOM.createPortal(
          <div
            className={classNames({
              "pointer-events-none fixed inset-0 z-50 bg-gray-900": true,
              "opacity-[0.02]": behavior === "hover",
              "opacity-[0.2]": behavior === "static",
            })}
          />,
          document.querySelector("#app") as HTMLDivElement
        )}

      {tooltipState === "shown" &&
        isClient &&
        ReactDOM.createPortal(
          <Popover.Panel
            ref={setPopperElement}
            style={{
              ...styles.popper,
              maxHeight,
              zIndex: 2147483001 + 1, // Above Intercom
            }}
            {...attributes.popper}
            static
            className="flex flex-col items-start space-y-2 overflow-hidden rounded-2xl bg-white text-left text-xs text-gray-900 shadow-2xl"
          >
            {content}
          </Popover.Panel>,
          document.querySelector("#app") as HTMLDivElement
        )}
    </Popover>
  );
};
